import * as React from 'react'

import { observer } from 'mobx-react'

import { cn } from '@/lib/utils'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  ICardListGroupConfig,
  ICardListItem,
  useCardListCore,
} from '~/client/src/shared/components/CardList'
import {
  GroupHeader,
  GroupHeaderTrigger,
} from '~/client/src/shared/components/GroupHeader'
import { iconLabel } from '~/client/src/shared/components/IconPicker/iconCatalog'
import IconPickerGrid from '~/client/src/shared/components/IconPicker/IconPickerGrid'
import IconPickerIconTile from '~/client/src/shared/components/IconPicker/IconPickerIconTile'
import IconPickerPanel from '~/client/src/shared/components/IconPicker/IconPickerPanel'
import {
  OptionsHeader,
  OptionsPanel,
} from '~/client/src/shared/components/OptionsList'
import { PickerLoading } from '~/client/src/shared/components/ResourcePicker/compound/PickerLoading'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import { useCreatableLocationKinds } from '~/client/src/shared/hooks/useCreatableLocationKinds'
import {
  PICTOGRAM_ICONS,
  PICTOGRAM_VIEW_BOX,
  type PictogramIconName,
  type PictogramSlug,
  toPictogramIconName,
} from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { InformationPointKind } from '~/client/src/shared/resources/domain/informationPointGroups'

import {
  iconItems,
  pickerGroupLabel,
  sortPickerGroups,
  typeItems,
} from './informationPointPickerItems'

const BY_TYPE_TAB = 'type'
const BY_ICON_TAB = 'icon'

interface IGroupedItem extends ICardListItem {
  group: string
}

interface IPlacement {
  kind: string | null
  iconName: string | null
}

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  anchor: HTMLElement | null
  placing: IPlacement
  onPlace: (
    kind: InformationPointKind,
    iconName: MapViewLocationIcon | PictogramIconName,
    isNamedByIcon?: boolean,
  ) => void
}

function PictogramArt({
  slug,
  className,
}: {
  slug: PictogramSlug
  className: string
}): React.ReactElement {
  const Icon = PICTOGRAM_ICONS[slug]
  return <Icon className={className} viewBox={PICTOGRAM_VIEW_BOX} />
}

function GroupedPanel<T extends IGroupedItem>({
  items,
  query,
  onQueryChange,
  count,
  renderGroup,
}: {
  items: T[]
  query: string
  onQueryChange: (query: string) => void
  count: (n: number) => string
  renderGroup: (items: T[]) => React.ReactNode
}): React.ReactElement {
  const groupConfig = React.useMemo<ICardListGroupConfig<T>>(
    () => ({
      groupBy: item => item.group,
      getGroupLabel: pickerGroupLabel,
      sortGroups: sortPickerGroups,
    }),
    [],
  )
  const listState = useCardListCore({ items, groupConfig })

  return (
    <IconPickerPanel
      query={query}
      onQueryChange={onQueryChange}
      count={listState.totalCount}
      countLabel={count(listState.totalCount)}
      listState={listState}
    >
      {listState.groups.map(group => (
        <section
          key={group.key}
          className="tw:border-0 tw:border-b tw:border-solid tw:border-input-soft"
        >
          <GroupHeader>
            <GroupHeaderTrigger
              expanded={!group.isCollapsed}
              onClick={() => listState.toggleGroup(group.key)}
              ariaLabel={group.label}
              label={group.label}
              count={group.count}
            />
          </GroupHeader>
          {!group.isCollapsed && renderGroup(group.items)}
        </section>
      ))}
    </IconPickerPanel>
  )
}

/**
 * Places an Information Point either by its type, drawn with that type's default icon, or by
 * any icon, the type then following from the icon, and the point named after it.
 */
function InformationPointPicker({
  open,
  onOpenChange,
  anchor,
  placing,
  onPlace,
}: IProps): React.ReactElement {
  const [query, setQuery] = React.useState('')
  const t = Localization.translator
  const place = (
    kind: InformationPointKind,
    iconName: MapViewLocationIcon | PictogramIconName,
    isNamedByIcon?: boolean,
  ) => {
    onPlace(kind, iconName, isNamedByIcon)
    onOpenChange(false)
  }
  const isPlacing = (kind: string, iconName: string) =>
    placing.kind === kind && placing.iconName === iconName

  const { isReady, isCreatable } = useCreatableLocationKinds()
  const types = React.useMemo(
    () => typeItems(query, isCreatable),
    [query, isCreatable],
  )
  const icons = React.useMemo(
    () => iconItems(query, isCreatable),
    [query, isCreatable],
  )

  return (
    <ResponsiveSurface
      open={open}
      onOpenChange={onOpenChange}
      title={t.informationPointPicker.title}
      anchor={anchor}
      side="bottom"
      sizing="fill"
      width="picker"
    >
      <OptionsPanel className="tw:flex-1 tw:py-2.5">
        <div className="tw:px-2.5">
          <OptionsHeader
            title={t.informationPointPicker.title}
            onClose={() => onOpenChange(false)}
          />
        </div>
        {isReady ? (
          <Tabs
            defaultValue={BY_TYPE_TAB}
            className="tw:min-h-0 tw:grow tw:basis-[640px] tw:gap-0"
          >
            <div className="tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-2.5">
              <TabsList variant="line" className="tw:w-full">
                <TabsTrigger value={BY_TYPE_TAB}>
                  {t.informationPointPicker.byType}
                </TabsTrigger>
                <TabsTrigger value={BY_ICON_TAB}>
                  {t.informationPointPicker.byIcon}
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent
              value={BY_TYPE_TAB}
              className="tw:flex tw:min-h-0 tw:flex-col"
            >
              <GroupedPanel
                items={types}
                query={query}
                onQueryChange={setQuery}
                count={t.informationPointPicker.xTypes}
                renderGroup={items => (
                  <div className="tw:flex tw:flex-col tw:p-1.5">
                    {items.map(({ id: kind, icon }) => (
                      <Button
                        key={kind}
                        type="button"
                        variant="ghost"
                        aria-pressed={isPlacing(
                          kind,
                          toPictogramIconName(icon),
                        )}
                        onClick={() => place(kind, toPictogramIconName(icon))}
                        className={cn(
                          'tw:h-auto tw:justify-start tw:gap-2.5 tw:px-2 tw:py-1.5 tw:text-left',
                          isPlacing(kind, toPictogramIconName(icon)) &&
                            'tw:bg-accent',
                        )}
                      >
                        <PictogramArt
                          slug={icon}
                          className="tw:size-8 tw:shrink-0"
                        />
                        <span className="tw:truncate tw:text-body-md tw:text-foreground">
                          {t.informationPointTypes[kind]}
                        </span>
                      </Button>
                    ))}
                  </div>
                )}
              />
            </TabsContent>
            <TabsContent
              value={BY_ICON_TAB}
              className="tw:flex tw:min-h-0 tw:flex-col"
            >
              <GroupedPanel
                items={icons}
                query={query}
                onQueryChange={setQuery}
                count={t.iconPicker.xIcons}
                renderGroup={items => (
                  <IconPickerGrid>
                    {items.map(({ id: iconName, kind }) => (
                      <IconPickerIconTile
                        key={iconName}
                        iconName={iconName}
                        label={iconLabel(iconName)}
                        typeName={t.informationPointTypes[kind]}
                        isSelected={isPlacing(kind, iconName)}
                        onPick={() => place(kind, iconName, true)}
                      />
                    ))}
                  </IconPickerGrid>
                )}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <PickerLoading />
        )}
      </OptionsPanel>
    </ResponsiveSurface>
  )
}

export default observer(InformationPointPicker)
